/**
 * @fileoverview Profile modal "Overview" tab.
 * @description Read-only account details grouped into Contact, Access and Account sections, a
 * session summary card (when the session was MFA-verified and when it expires) that links over to
 * the Security tab, and the user's privileges as chips. Everything shown comes from GET
 * /identity/me and the sign-in session already in the auth store: the identity backend serves no
 * phone, department, joining date or manager, so none of those are shown. Tenant, access level and
 * banking scope are rendered only when present, since the real backend doesn't serve them yet.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useTranslation } from 'react-i18next'

import { formatBankingScope } from '@/features/auth/utils/profile-display.utils'
import LinkButton from '@/shared/components/atoms/LinkButton'
import DetailField from '@/shared/components/molecules/DetailField'
import DetailSectionHeading from '@/shared/components/molecules/DetailSectionHeading'
import { toTestIdSlug } from '@/shared/lib/test-id/test-id.utils'

import type { CurrentPrincipal } from '@/features/auth/services/session.service'

const VISIBLE_PRIVILEGE_COUNT = 6

interface ProfileOverviewTabProps {
  email: string
  roleName?: string | undefined
  principal?: CurrentPrincipal | undefined
  onManageSecurity: () => void
}

/**
 * @component
 * @param {{ session: NonNullable<CurrentPrincipal['session']>, onManage: () => void }} props - The
 * sign-in session and the Security-tab shortcut.
 * @param {NonNullable<CurrentPrincipal['session']>} props.session - When it was MFA-verified and
 * when it expires.
 * @param {() => void} props.onManage - Switches the modal to its Security tab.
 */
function ProfileSessionSummary({
  session,
  onManage,
}: {
  session: NonNullable<CurrentPrincipal['session']>
  onManage: () => void
}) {
  const { t } = useTranslation()

  return (
    <div
      className="flex items-center gap-3.5 rounded-xl bg-input-bg px-4 py-3.5"
      data-testid="profile-session-summary"
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-ink">{t('profile.sessionSummary.title')}</p>
        <p className="mt-0.5 text-compact-sm text-slate">
          {t('profile.sessionSummary.verifiedAt', {
            date: new Date(session.mfaVerifiedAt).toLocaleString(),
          })}
          {' · '}
          {t('profile.sessionSummary.expiresAt', {
            date: new Date(session.absoluteExpiresAt).toLocaleString(),
          })}
        </p>
      </div>
      <LinkButton size="sm" onClick={onManage} data-testid="profile-session-manage-button">
        {t('profile.sessionSummary.manage')}
      </LinkButton>
    </div>
  )
}

/**
 * @component
 * @param {{ privileges: string[] }} props - The user's privilege keys.
 * @param {string[]} props.privileges - Shown as chips: the first few, then a count for the rest;
 * nothing renders when there are none.
 */
function ProfilePrivileges({ privileges }: { privileges: string[] }) {
  const { t } = useTranslation()

  if (privileges.length === 0) {
    return null
  }

  return (
    <section
      className="flex flex-col gap-3 border-t border-divider pt-5"
      data-testid="profile-privileges"
    >
      <DetailSectionHeading>{t('profile.privilegesTitle')}</DetailSectionHeading>
      <ul className="flex flex-wrap gap-2">
        {privileges.slice(0, VISIBLE_PRIVILEGE_COUNT).map((privilege) => (
          <li
            key={privilege}
            data-testid={`profile-privilege-${toTestIdSlug(privilege)}`}
            className="rounded-full bg-surface-muted px-3 py-1 text-compact-sm text-label"
          >
            {privilege}
          </li>
        ))}
        {privileges.length > VISIBLE_PRIVILEGE_COUNT && (
          <li
            className="rounded-full bg-surface-muted px-3 py-1 text-compact-sm font-semibold text-slate"
            data-testid="profile-privileges-more"
          >
            {t('profile.privilegesMore', { count: privileges.length - VISIBLE_PRIVILEGE_COUNT })}
          </li>
        )}
      </ul>
    </section>
  )
}

/**
 * @component
 * @param {ProfileOverviewTabProps} props - The identity details and the security-tab shortcut.
 * @param {string} props.email - The signed-in user's email address.
 * @param {string} [props.roleName] - The user's active role name.
 * @param {CurrentPrincipal} [props.principal] - GET /identity/me result, when loaded.
 * @param {() => void} props.onManageSecurity - Switches the modal to its Security tab.
 */
function ProfileOverviewTab({
  email,
  roleName,
  principal,
  onManageSecurity,
}: ProfileOverviewTabProps) {
  const { t } = useTranslation()
  const privileges = principal?.privileges ?? []
  const session = principal?.session ?? null
  const showScope =
    principal?.bankingScope !== undefined ||
    privileges.some((privilege) => privilege.startsWith('banking:'))

  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col gap-3">
        <DetailSectionHeading>{t('profile.sectionContact')}</DetailSectionHeading>
        <DetailField label={t('profile.emailLabel')} data-testid="profile-email">
          <span className="break-all">{email}</span>
        </DetailField>
      </section>

      <section className="flex flex-col gap-3 border-t border-divider pt-5">
        <DetailSectionHeading>{t('profile.sectionAccess')}</DetailSectionHeading>
        <div className="grid grid-cols-2 gap-x-6 gap-y-4">
          <DetailField label={t('profile.roleLabel')} data-testid="profile-access-role">
            {roleName ?? '—'}
          </DetailField>
          {principal?.activeRoleLevel !== undefined && (
            <DetailField label={t('profile.accessLevelLabel')} data-testid="profile-access-level">
              {principal.activeRoleLevel}
            </DetailField>
          )}
          {principal?.tenantId && (
            <DetailField label={t('profile.tenantLabel')} data-testid="profile-tenant">
              {principal.tenantId}
            </DetailField>
          )}
          {showScope && (
            <DetailField label={t('profile.scopeLabel')} data-testid="profile-scope">
              {principal?.bankingScope
                ? formatBankingScope(t, principal.bankingScope)
                : t('profile.scopeUnrestricted')}
            </DetailField>
          )}
        </div>
      </section>

      {principal && (
        <section className="flex flex-col gap-3 border-t border-divider pt-5">
          <DetailSectionHeading>{t('profile.sectionAccount')}</DetailSectionHeading>
          <DetailField label={t('profile.userIdLabel')} data-testid="profile-user-id">
            <span className="break-all font-mono text-compact-sm">{principal.userId}</span>
          </DetailField>
          {session && <ProfileSessionSummary session={session} onManage={onManageSecurity} />}
        </section>
      )}

      <ProfilePrivileges privileges={privileges} />
    </div>
  )
}

export default ProfileOverviewTab
